import type { FC } from 'react';
import React, { useState, useEffect, useRef } from 'react';

import classNames from 'clsx';
import {
  formatClassNames,
  getDataAttributes,
} from '@wix/editor-elements-common-utils';

import type { ILoginSocialBarProps } from '../../LoginSocialBar.types';
import LoggedInSocialBar from './components/loggedInSocialBar';
import LoggedOffSocialBar from './components/loggedOffSocialBar';
import { SkeletonBar } from './components/skeletonBar';
import type { BuyAgainProps } from './components/buyAgain';
import {
  BIAction,
  getMembersAreaNavigationEvent,
  MEMBERS_APP_UOU_ENDPOINT,
} from '../bi/events';

import styles from '../style/LoginSocialBar.module.scss';
import semanticClassNames from './LoginSocialBar.semanticClassNames';

const LoginSocialBar: FC<ILoginSocialBarProps> = props => {
  const {
    id,
    className,
    customClassNames = [],
    direction,
    isLoggedIn,
    userName,
    loggedInTextState,
    loggedInText,
    customLoggedInText,
    menuItems,
    iconItems,
    currentPrimaryPageHref,
    reportBiOnClick,
    onLogin,
    onLogout,
    onMouseEnter,
    onMouseLeave,
    isMenuOpen,
    onMenuOpen,
    onMenuClose,
    reportBiOnMenuItemClick,
    translations,
    avatarUri,
    reportBi,
    loggedInAvatarIcon,
    loggedOutAvatarIcon,
    loggedInCustomAvatar,
    loggedOutCustomAvatar,
    enableLoggingInStateInSSR,
    enableSkeletonLoader,
    useNewElementStructure,
    useNestedMenuItems,
    onBlur,
    withNativeThemeOverrides = false,
    withNewColorWiring = false,
    useNewDropdownMenuAlignment,
    buyAgainOrderItems,
    buyAgainHeaderText,
    buyAgainAddToCartText,
    onBuyAgainAddToCart,
    buyAgainConfirmedItem,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- TODO: re-reference when restoring the showBuyAgain guard below
    showBuyAgain = false,
    buyAgainCount = 1,
  } = props;

  const [isHydrated, setIsHydrated] = useState(false);
  const rootElementRef = useRef<HTMLDivElement>(null);

  // only show logged in once hydration finishes for caching
  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const onHover = (event: React.MouseEvent<Element, MouseEvent>) => {
    onMouseEnter?.(event);
    if (isLoggedIn && !isMenuOpen) {
      void reportBi(getMembersAreaNavigationEvent({ action: BIAction.Hover }), {
        endpoint: MEMBERS_APP_UOU_ENDPOINT,
      });
    }
  };

  const openMenu = () => {
    onMenuOpen();
    if (isLoggedIn) {
      void reportBi(
        getMembersAreaNavigationEvent({ action: BIAction.MenuClick }),
        {
          endpoint: MEMBERS_APP_UOU_ENDPOINT,
        },
      );
    }
  };

  const shouldShowLoggedIn = enableLoggingInStateInSSR
    ? isHydrated && isLoggedIn
    : isLoggedIn;

  // TODO: temporary — Buy Again is forced on for testing regardless of
  // compData.showBuyAgain. Restore the `showBuyAgain &&` guard before merging.
  const buyAgainProps: BuyAgainProps | undefined =
    onBuyAgainAddToCart && buyAgainOrderItems?.length
      ? {
          orderItems: buyAgainOrderItems.slice(0, buyAgainCount),
          headerText: buyAgainHeaderText ?? translations.buyAgainHeaderText!,
          addToCartText:
            buyAgainAddToCartText ?? translations.buyAgainAddToCartText!,
          onAddToCart: onBuyAgainAddToCart,
          confirmedItem: buyAgainConfirmedItem,
        }
      : undefined;

  const shouldShowSkeleton =
    enableLoggingInStateInSSR && enableSkeletonLoader && !isHydrated;

  const renderBar = () => {
    if (shouldShowSkeleton) {
      return <SkeletonBar />;
    }
    if (shouldShowLoggedIn) {
      return (
        <LoggedInSocialBar
          avatarUri={avatarUri}
          isLoggedIn={isLoggedIn}
          userName={userName}
          id={id}
          rootElement={rootElementRef.current}
          loggedInTextState={loggedInTextState}
          loggedInText={loggedInText}
          customLoggedInText={customLoggedInText}
          currentPrimaryPageHref={currentPrimaryPageHref}
          menuItems={menuItems}
          iconItems={iconItems}
          renderIcons={!!iconItems.length}
          isMenuOpen={isMenuOpen}
          onMenuOpen={openMenu}
          reportBiOnMenuItemClick={reportBiOnMenuItemClick}
          onLogout={onLogout}
          onMenuClose={onMenuClose}
          avatarIcon={loggedInAvatarIcon}
          customAvatar={loggedInCustomAvatar}
          translations={translations}
          reportBi={reportBi}
          reportBiOnClick={reportBiOnClick}
          useNewElementStructure={useNewElementStructure}
          useNestedMenuItems={useNestedMenuItems}
          withNativeThemeOverrides={withNativeThemeOverrides}
          withNewColorWiring={withNewColorWiring}
          useNewDropdownMenuAlignment={useNewDropdownMenuAlignment}
          buyAgainProps={buyAgainProps}
        />
      );
    }
    return (
      <LoggedOffSocialBar
        id={id}
        avatarIcon={loggedOutAvatarIcon}
        customAvatar={loggedOutCustomAvatar}
        handleLogin={onLogin}
        reportBiOnClick={reportBiOnClick}
        translations={translations}
        useNewElementStructure={useNewElementStructure}
        withNewColorWiring={withNewColorWiring}
      />
    );
  };

  return (
    <div
      id={id}
      {...getDataAttributes(props)}
      className={classNames(
        formatClassNames(semanticClassNames.root, ...customClassNames),
        semanticClassNames.root,
        styles.root,
        className,
        {
          [styles.withNewColorWiring]: withNewColorWiring,
        },
      )}
      dir={direction}
      tabIndex={-1}
      onBlur={isLoggedIn && onBlur ? onBlur : undefined}
      ref={rootElementRef}
      onMouseEnter={onHover}
      onMouseLeave={onMouseLeave}
    >
      {renderBar()}
    </div>
  );
};

export default LoginSocialBar;
